/**
 * WHAT THIS COLUMN IS FOR, shown when there is nothing in it yet.
 *
 * Owner, 2026-09-20: 「當使用者在還沒有任何 Artifact 的時候,它的 Side Panel Artifact
 * 的那一個欄位,上面我們應該要寫一些使用的使用者指南,讓使用者可以知道他可以讓他的
 * Agent 使用 Avengents Artifact 這一項功能,這樣子他的使用者就知道那個欄位存在的意義
 * 是什麼」.
 *
 * The empty state used to be one line of CLI: "Nothing published yet. On a
 * machine, run `avengents artifact add ./site`." That is an instruction for
 * somebody who already knows what an artifact is, printed for the only person
 * who by definition does not — everyone reading it has never published one. An
 * empty section that explains nothing is a section whose purpose is invisible.
 *
 * ASKING AN AGENT LEADS, and that is the owner's point rather than a nicety.
 * Every machine here already has agents running on it, they can all run this
 * command, and a person who does not live in a terminal has no reason to guess
 * that. The typed commands stay, second, for whoever wants them.
 *
 * TWO EMPTY STATES, NOT ONE. "You publish nothing" and "you have no machines"
 * are different sentences, and only one of them is fixed by publishing
 * something: telling a person with no paired machine to run a command "on a
 * machine" sends them to a place that does not exist. The same rule the rest of
 * this panel follows for a machine that could not be asked.
 */
export function ArtifactsGuide({ hasMachines }: { hasMachines: boolean }) {
  if (!hasMachines) {
    return (
      <div className="ag">
        <p className="ag-lead">
          Artifacts put a page or an app that runs on <em>your own computer</em> at a web address
          you can share — nothing is uploaded, and every visit is carried live to that machine.
        </p>
        <p className="more-note">
          This account has no machines paired yet, so there is nothing to publish from. Pair one
          from the Avengents menu at the top of this panel, then come back here.
        </p>
      </div>
    )
  }

  return (
    <div className="ag">
      <p className="ag-lead">
        Artifacts put a page or an app that runs on <em>your own machine</em> at a web address you
        can share — nothing is uploaded, and every visit is carried live to that machine. When the
        machine is off, the address says so.
      </p>

      <h4 className="ag-h">The quickest way: ask an agent</h4>
      <p className="ag-p">
        Any agent on one of your machines can publish for you. Say something like:
      </p>
      {/* Plain, quotable, and deliberately not a command: the point is that this
          is a sentence a person can say, not a syntax they have to get right. */}
      <p className="ag-say">
        &ldquo;Publish this folder as an Avengents artifact and tell me the link.&rdquo;
      </p>
      <p className="ag-p">
        It works for something already running too — &ldquo;publish the app on port 5173&rdquo;.
      </p>

      <h4 className="ag-h">Or do it yourself</h4>
      <p className="ag-p">On the machine itself:</p>
      <ul className="ag-cmds">
        <li>
          <code>avengents artifact add ./site</code> — a folder of pages
        </li>
        <li>
          <code>avengents artifact add --port 5173</code> — an app already running
        </li>
      </ul>

      <h4 className="ag-h">Then it appears here</h4>
      <p className="ag-p">
        Every artifact you publish shows up in this list with the machine it came from and whether
        that machine is reachable. Tap one to open it, hand out the link, or choose who may open
        it — only you, friends you pick, or anyone with the link. Everything starts private.
      </p>
    </div>
  )
}
